/**
 * Whole-point weights that share 100 — the one allocator.
 *
 * Objective weights (roadmap P-9, PRD-objective-weights-and-strategic-
 * coverage §3.2), strategic-driver weights and session criteria weights all
 * mean the same thing: a set of shares of one whole. They use this module
 * instead of each rounding its own way. Every result is whole numbers that
 * sum to exactly 100 (or every weight 0 when there is nothing to share).
 *
 * Pure. Weights are keyed by id so callers keep their own row shapes.
 */

export type WeightMap = Record<string, number>;

export const WEIGHT_TOTAL = 100;

export function totalWeight(weights: WeightMap): number {
  return Object.values(weights).reduce((s, w) => s + w, 0);
}

export function isBalanced(weights: WeightMap): boolean {
  return Object.keys(weights).length > 0 && totalWeight(weights) === WEIGHT_TOTAL;
}

/**
 * Distribute `total` whole points in proportion to `shares`, settling the
 * rounding remainder on the largest fractional parts (ties: the larger share,
 * then id order) so the result always sums to exactly `total`.
 *
 * Exported for a set that shares less than 100 (a session's track criteria
 * beside criteria of its own, sessionCriteriaFromTrack.ts). Migration 1048
 * apportions a session's Event Designer weights the same way on the server.
 */
export function apportion(shares: [string, number][], total: number): WeightMap {
  const sum = shares.reduce((s, [, v]) => s + v, 0);
  const out: WeightMap = {};
  if (shares.length === 0) return out;
  if (sum <= 0) {
    // Nothing to be proportional to: split evenly.
    return apportion(
      shares.map(([id]) => [id, 1]),
      total,
    );
  }
  const raw = shares.map(([id, v]) => ({ id, v, exact: (v / sum) * total }));
  let given = 0;
  for (const r of raw) {
    out[r.id] = Math.floor(r.exact);
    given += out[r.id];
  }
  const order = [...raw].sort(
    (a, b) =>
      b.exact - Math.floor(b.exact) - (a.exact - Math.floor(a.exact)) ||
      b.v - a.v ||
      a.id.localeCompare(b.id),
  );
  for (let i = 0; given < total; i = (i + 1) % order.length) {
    out[order[i].id] += 1;
    given += 1;
  }
  return out;
}

/** The same share for every id, the remainder going to the first ids. */
export function evenSplit(ids: string[]): WeightMap {
  return apportion(
    ids.map((id) => [id, 1]),
    WEIGHT_TOTAL,
  );
}

/** Scale to 100 keeping the proportions; negative weights count as 0. */
export function normalizeTo100(weights: WeightMap): WeightMap {
  const entries = Object.entries(weights).map(([id, w]) => [id, Math.max(0, w)] as [string, number]);
  return apportion(entries, WEIGHT_TOTAL);
}

export interface Reallocation {
  weights: WeightMap;
  /** Set when the change could not be absorbed in full and was clamped. */
  clampedReason: string | null;
}

/**
 * Set one weight and let the other unlocked weights absorb the difference in
 * proportion to their current size, so the set still sums to 100. When the
 * others cannot absorb it (they are locked, or the change is larger than what
 * is free), the new value is clamped to what is possible and the reason is
 * returned rather than producing a set that does not sum to 100.
 *
 * Expects a balanced set; normalize first if it is not.
 */
export function reallocate(
  weights: WeightMap,
  changedId: string,
  newValue: number,
  lockedIds: ReadonlySet<string> = new Set(),
): Reallocation {
  const ids = Object.keys(weights);
  const free = ids.filter((id) => id !== changedId && !lockedIds.has(id));
  const lockedTotal = ids
    .filter((id) => id !== changedId && lockedIds.has(id))
    .reduce((s, id) => s + weights[id], 0);

  const max = free.length === 0 ? weights[changedId] : WEIGHT_TOTAL - lockedTotal;
  const min = free.length === 0 ? weights[changedId] : 0;
  const wanted = Math.round(newValue);
  const value = Math.min(max, Math.max(min, wanted));
  let clampedReason: string | null = null;
  if (value !== wanted) {
    clampedReason =
      free.length === 0
        ? 'Every other weight is locked, so this one cannot change.'
        : `Locked weights hold ${lockedTotal}, so this one can be at most ${max}.`;
  }

  const remaining = WEIGHT_TOTAL - lockedTotal - value;
  const out: WeightMap = { ...weights, [changedId]: value };
  const spread = apportion(
    free.map((id) => [id, weights[id]]),
    remaining,
  );
  Object.assign(out, spread);
  return { weights: out, clampedReason };
}
